// Fan-out of a cluster at close zoom ("Group into a count", spec
// 2026-10-30-map-label-crowding-and-detail §3.4). Pure geometry + GeoJSON
// building; MapView owns the map, the source and the close rules.
//
// Offsets are screen pixels from the cluster point, clockwise from the top
// (+dx right, +dy down — MapLibre's screen axes).
//
// Geometry decisions (the brief left these open):
// - Ring (≤ 8 leaves): radius is `ringRadiusPx`, grown when needed so two
//   neighbours are at least two badge heights apart (a marker and its label
//   below it): r = max(ringRadiusPx, 2·badgeHeightPx / (2·sin(π/n))). With the
//   defaults (24 / 44) that grows 6 leaves (→ 48 px), 7 (→ ≈ 55 px) and 8
//   (→ ≈ 63 px).
// - Spiral (> 8 leaves): Archimedean r = ringRadiusPx + b·θ with b chosen so
//   successive turns are 2·badgeHeightPx apart, and points stepped every
//   1.5·badgeHeightPx of arc length. The chord of that step is ≥ 1.4·badge
//   height at the smallest radius, so consecutive points stay a badge height
//   apart; 50 leaves fit inside a ≈ 170 px radius at the defaults.

export const FAN_MAX = 50;

const RING_MAX = 8;
// Drop the -0 that sin/cos produce so offsets compare cleanly.
const clean = (n) => (Math.abs(n) < 1e-9 ? 0 : n);
const at = (r, theta) => ({ dx: clean(r * Math.sin(theta)), dy: clean(-r * Math.cos(theta)) });

export function fanOffsets(count, { badgeHeightPx = 24, ringRadiusPx = 44 } = {}) {
  const n = Math.max(0, Math.min(Math.floor(count) || 0, FAN_MAX));
  if (n === 0) return [];
  if (n === 1) return [at(ringRadiusPx, 0)];
  if (n <= RING_MAX) {
    const minGap = 2 * badgeHeightPx;
    const r = Math.max(ringRadiusPx, minGap / (2 * Math.sin(Math.PI / n)));
    return Array.from({ length: n }, (_, i) => at(r, (2 * Math.PI * i) / n));
  }
  const b = (2 * badgeHeightPx) / (2 * Math.PI);
  const step = 1.5 * badgeHeightPx;
  const out = [];
  let theta = 0;
  for (let i = 0; i < n; i++) {
    const r = ringRadiusPx + b * theta;
    out.push(at(r, theta));
    theta += step / Math.hypot(r, b);
  }
  return out;
}

export function fanClickDecision({ currentZoom, expansionZoom, maxZoom = 22, leavesCoLocated: coLocated }) {
  if (currentZoom >= 17 || expansionZoom > maxZoom || coLocated) return 'fan';
  return 'zoom';
}

export function leavesCoLocated(leaves, epsilonDeg = 1e-5) {
  if (!leaves?.length) return false;
  const [lng0, lat0] = leaves[0].geometry.coordinates;
  return leaves.every(({ geometry: { coordinates: [lng, lat] } }) =>
    Math.abs(lng - lng0) <= epsilonDeg && Math.abs(lat - lat0) <= epsilonDeg);
}

const toLngLatArray = (p) => (Array.isArray(p) ? p : [p.lng, p.lat]);

// Clockwise from the top in 45° sectors: the label sits on the far side of the
// leaf from the cluster, so the anchor names the label's side facing the leaf.
const FAN_ANCHORS = ['bottom', 'bottom-left', 'left', 'top-left', 'top', 'top-right', 'right', 'bottom-right'];

/** `text-anchor` that puts a leaf's label outward, away from the cluster. */
export function fanAnchor({ dx, dy }) {
  const theta = Math.atan2(dx, -dy); // 0 = up, clockwise (screen y grows down)
  const sector = Math.round(theta / (Math.PI / 4));
  return FAN_ANCHORS[((sector % 8) + 8) % 8];
}

/** Of the clicked features, the fan leaf whose point projects nearest to `point`
 *  (within `maxPx`), or null. Overlapping leaf icons and labels mean the first
 *  hit is often a neighbour. */
export function nearestFanLeaf(features, point, project, maxPx = Infinity) {
  let best = null;
  let bestD = maxPx;
  for (const f of features ?? []) {
    if (f?.properties?.kind !== 'fan-leaf' || f.geometry?.type !== 'Point') continue;
    const p = project(f.geometry.coordinates);
    const d = Math.hypot(p.x - point.x, p.y - point.y);
    if (d <= bestD) { best = f; bestD = d; }
  }
  return best;
}

/**
 * `project(lngLat) → {x, y}`, `unproject({x, y}) → [lng, lat]` (a LngLat object
 * is accepted too). Leaves keep their properties; `kind` becomes 'fan-leaf' and
 * the original kind ('vehicle' | 'staff') moves to `leafKind`; `fanAnchor` is
 * the leaf label's outward `text-anchor`. `total` is the
 * cluster's real size (`point_count`; the leaves are capped at FAN_MAX) and
 * defaults to `leaves.length`; when it exceeds the leaves shown, a 'fan-center'
 * point carries `extraLabel`.
 */
export function buildFanFC(centerLngLat, leaves, offsets, project, unproject, { total = leaves.length, extraLabel = (n) => `+${n}` } = {}) {
  const center = toLngLatArray(centerLngLat);
  const c = project(center);
  const shown = Math.min(leaves.length, offsets.length);
  const features = [];
  for (let i = 0; i < shown; i++) {
    const { dx, dy } = offsets[i];
    const pos = toLngLatArray(unproject({ x: c.x + dx, y: c.y + dy }));
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [center, pos] },
      properties: { kind: 'fan-leg' },
    });
    const props = leaves[i].properties ?? {};
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: pos },
      properties: { ...props, leafKind: props.kind, kind: 'fan-leaf', fanAnchor: fanAnchor(offsets[i]) },
    });
  }
  const extra = Math.max(0, total - shown);
  if (extra > 0) {
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: center },
      properties: { kind: 'fan-center', extra, extraLabel: extraLabel(extra) },
    });
  }
  return { fc: { type: 'FeatureCollection', features }, extra };
}
